/* =========================================================================
   javy-cart-drawer.js — simplified event-driven version

   What this file does:
   1. Updates cart lines through Shopify Ajax /cart/change.js.
   2. Changes selling_plan directly on existing lines; no remove + re-add.
   3. Keeps subscription upgrade logic available, but does not auto-sync frequency on quantity changes.
   4. Handles the "Save with Subscription" CTA.
   5. Refreshes the cart drawer from Shopify-rendered sections.

   Important:
   - No morphdom.
   - No MutationObserver.
   - No window.fetch interception.
   - No Cart Transform dependency.
   ========================================================================= */

(function () {
  const SELECTORS = {
    drawer: 'cart-drawer',
    drawerInner: 'cart-drawer .drawer__inner',
    cartLine: '.cart_line-container',
    loader: 'cart-drawer .cart-loader',
    subscribeCheckout: '#CartDrawer-SubscribeCheckout',
    checkoutForm: '#CartDrawer-Form',
    // cartIconBubble: '#cart-icon-bubble'
  };

  let activeJob = Promise.resolve();
  let isBusy = false;

  function root() {
    return window.Shopify?.routes?.root || '/';
  }

  function toNumber(value, fallback = 0) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function parseSellingPlanId(value) {
  if (value === null) return null;
  if (value === undefined || value === '') return undefined;

  const stringValue = String(value).trim();

  // Handles: gid://shopify/SellingPlan/123456789
  const gidMatch = stringValue.match(/SellingPlan\/(\d+)$/);
  if (gidMatch) return Number(gidMatch[1]);

  // Handles: 123456789
  const numericMatch = stringValue.match(/\d+/);
  if (numericMatch) return Number(numericMatch[0]);

  return undefined;
}

  function getLines() {
    return Array.from(document.querySelectorAll(SELECTORS.cartLine));
  }

  function showLoader(show) {
    const loader = document.querySelector(SELECTORS.loader);
    if (loader) loader.classList.toggle('is-active', Boolean(show));
  }

  function getFrequencyRules() {
    const script = document.getElementById('CartDrawerSubscriptionFrequencyRules');
    if (!script) return {};

    try {
      return JSON.parse(script.textContent || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  function tierForQty(qty) {
    if (qty >= 4) return '4';
    if (qty >= 3) return '3';
    if (qty >= 2) return '2';
    return '1';
  }

  function getPlanIdForFamilyQty(line, familyQty) {
    const rules = getFrequencyRules();
    const family = line.dataset.productFamily;
    const tier = tierForQty(familyQty);
    const days = Number(rules?.[family]?.[tier]?.days || 30);

    if (days === 60) return line.getAttribute('data-selling-plan-60');
    if (days === 45) return line.getAttribute('data-selling-plan-45');
    return line.getAttribute('data-selling-plan-30');
  }

  function collectFamilyQty(lines, options = {}) {
    const { subscriptionsOnly = false } = options;
    const qtyByFamily = {};

    lines.forEach((line) => {
      const family = line.dataset.productFamily;
      const qty = toNumber(line.dataset.quantity, 0);
      const hasSellingPlan = Boolean(line.dataset.currentSellingPlan);

      if (!family || qty <= 0) return;
      if (subscriptionsOnly && !hasSellingPlan) return;

      qtyByFamily[family] = (qtyByFamily[family] || 0) + qty;
    });

    return qtyByFamily;
  }

  async function changeCartLine({ key, line, quantity, sellingPlan, sections = false }) {
    const body = {
      quantity: toNumber(quantity, 1)
    };

    const cleanKey = key ? String(key).trim() : '';

    if (cleanKey) {
      body.id = cleanKey;
    } else if (line !== undefined && line !== null) {
      const lineNumber = toNumber(line, null);
      if (lineNumber) body.line = lineNumber;
    }

    if (!body.id && !body.line) {
      console.error('[Javy] Missing cart line identifier before /cart/change.js', {
        key,
        line,
        quantity,
        body
      });
      throw new Error('Missing cart line id or line number');
    }

    const parsedSellingPlan = parseSellingPlanId(sellingPlan);

    if (parsedSellingPlan !== undefined) {
      body.selling_plan = parsedSellingPlan;
    }

    if (sections) {
      body.sections = ['cart-drawer'];
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    });

    const text = await response.text().catch(() => '');

    if (!response.ok) {
      console.error('[cart/change.js failed]', {
        status: response.status,
        body,
        response: text
      });

      throw new Error(text || 'Cart line update failed');
    }

    try {
      return JSON.parse(text);
    } catch {
      return {};
    }
  }

  async function fetchSections() {
    const response = await fetch(
      // root() + '?sections=cart-drawer,cart-icon-bubble&_=' + Date.now(),
      root() + '?sections=cart-drawer&_=' + Date.now(),
      { cache: 'no-store', headers: { Accept: 'application/json' } }
    );
    if (!response.ok) throw new Error('Could not fetch cart sections');
    return response.json();
  }

  function getCartLineNumber(lineElement) {
  const lines = getLines();
  const index = lines.indexOf(lineElement);

  // Shopify Ajax line numbers are 1-based.
  return index >= 0 ? index + 1 : null;
}

  function getSectionElement(sectionHTML, selector) {
    if (!sectionHTML) return null;
    const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    return doc.querySelector(selector);
  }

  function replaceCartDrawer(sectionHTML) {
    const currentInner = document.querySelector(SELECTORS.drawerInner);
    const freshInner = getSectionElement(sectionHTML, '.drawer__inner');

    if (currentInner && freshInner) {
      currentInner.innerHTML = freshInner.innerHTML;
    }
  }

  function setCartCount(count) {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!bubble) return;

    bubble.hidden = count === 0;
    bubble.classList.toggle('is-active', count > 0);

    const num = bubble.querySelector('[data-cart-count]');
    if (num) num.textContent = count > 0 && count < 100 ? count : '';
  }

  async function refreshCartDrawer(sectionsFromCart) {
    const sections = sectionsFromCart || await fetchSections();

    replaceCartDrawer(sections['cart-drawer']);
    // replaceCartIconBubble(sections['cart-icon-bubble']);
    setCartCount(readCartCountFromDrawer());

    document.dispatchEvent(new CustomEvent('javy:cart-drawer-refreshed'));
  }

  function readCartCountFromDrawer() {
    const el = document.querySelector('cart-drawer .cart_header-quantity');
    return toNumber(el && el.textContent, 0);
  }

  async function runCartJob(job, options = {}) {
    const { loader = false } = options;

    activeJob = activeJob
      .catch(() => {})
      .then(async () => {
        isBusy = true;
        if (loader) showLoader(true);

        try {
          return await job();
        } finally {
          if (loader) showLoader(false);
          isBusy = false;
        }
      });

    return activeJob;
  }

async function syncSubscriptionPlans(options = {}) {

  const { refresh = true } = options;
  let totalChanged = false;
  let lastCart = null;
  let iterations = 0;
  const MAX_ITERATIONS = 10;

  while (iterations < MAX_ITERATIONS) {
    iterations++;

    const lines = getLines();

    const debugLines = lines.map((line, index) => ({
      index: index + 1,
      title: line.querySelector('.cart_line-title')?.textContent?.trim(),
      variantId: line.dataset.variantId,
      quantity: line.dataset.quantity,
      family: line.dataset.productFamily,
      currentPlan: line.dataset.currentSellingPlan,
      plan30: line.getAttribute('data-selling-plan-30'),
      plan45: line.getAttribute('data-selling-plan-45'),
      plan60: line.getAttribute('data-selling-plan-60'),
      key: line.dataset.key
    }));

    const subscriptionQtyByFamily = collectFamilyQty(lines, { subscriptionsOnly: true });

    let changedThisPass = false;

    for (const line of lines) {
      const currentPlan = line.dataset.currentSellingPlan;
      const family = line.dataset.productFamily;
      const key = line.dataset.key;
      const quantity = toNumber(line.dataset.quantity, 1);

      if (!currentPlan || !family || !key || quantity <= 0) continue;

      const familyQty = subscriptionQtyByFamily[family] || quantity;
      const targetPlan = getPlanIdForFamilyQty(line, familyQty);

      if (!targetPlan || String(targetPlan) === String(currentPlan)) continue;

      lastCart = await changeCartLine({
        line: getCartLineNumber(line),
        quantity,
        sellingPlan: targetPlan,
        sections: false
      });

      changedThisPass = true;
      totalChanged = true;

      // Patch data attributes on live DOM elements from the fresh section HTML
      // so the next iteration reads correct plan IDs without a visible re-render
      line.dataset.currentSellingPlan = String(targetPlan);

      const updatedLineNumber = getCartLineNumber(line);

      if (lastCart?.items?.[updatedLineNumber - 1]?.key) {
        line.dataset.key = lastCart.items[updatedLineNumber - 1].key;
      }

      break; // restart for loop with updated DOM attributes
    }

    if (!changedThisPass) {
      break;
    }
  }

  // Single DOM refresh at the very end — only one visible update regardless of how many lines changed
  if (refresh && totalChanged) {
    await refreshCartDrawer();
  }


  return { changed: totalChanged, cart: lastCart };
}

  async function setLineQuantity(line, quantity) {
    if (!line) return;

    const qty = Math.max(0, toNumber(quantity, 0));

    const originalLineKey = line.dataset.key ? String(line.dataset.key).trim() : '';
    const originalLineIndex =
      line.dataset.line ||
      line.querySelector('[data-index]')?.dataset.index ||
      null;

    const originalVariantId = line.dataset.variantId
      ? String(line.dataset.variantId).trim()
      : '';

    let freshLine = line;

    if (!freshLine.isConnected) {
      if (originalLineIndex) {
        freshLine =
          document
            .querySelector(`#Drawer-quantity-${originalLineIndex}`)
            ?.closest(SELECTORS.cartLine) || freshLine;
      }

      if ((!freshLine || !freshLine.isConnected) && originalVariantId) {
        freshLine =
          Array.from(document.querySelectorAll(SELECTORS.cartLine))
            .find(el => String(el.dataset.variantId).trim() === originalVariantId) || freshLine;
      }
    }

    const lineKey = freshLine?.dataset?.key
      ? String(freshLine.dataset.key).trim()
      : originalLineKey;

    const lineIndex =
      freshLine?.dataset?.line ||
      freshLine?.querySelector?.('[data-index]')?.dataset.index ||
      originalLineIndex;

    if (!lineKey && !lineIndex) {
      console.error('[Javy] Cannot update quantity: missing line key and line index', {
        line,
        freshLine,
        quantity: qty
      });
      throw new Error('Missing cart line key/index');
    }

    const removedItem = qty === 0 && freshLine
      ? {
          title: freshLine.querySelector('.cart_line-title')?.textContent?.trim() || '',
          variant: Number(freshLine.dataset.variantId || 0),
          qty: Number(freshLine.dataset.quantity || 1)
        }
      : null;

    if (removedItem && removedItem.variant) {
      window.KinokoAtcTracker?.trackRemove(removedItem);
    }

    await runCartJob(async () => {
      const cart = await changeCartLine({
        key: lineKey || undefined,
        line: lineKey ? undefined : lineIndex,
        quantity: qty,
        sections: true
      });

      await refreshCartDrawer(cart?.sections);
    });
  }

  async function upgradeCartToSubscription() {
    await runCartJob(async () => {
      // 1. Resolve target plan per variant from the live DOM (stable across re-key).
      //    Build this BEFORE any mutation, while the rendered lines are trustworthy.
      const liveLines = getLines();
      const familyQty = collectFamilyQty(liveLines, { subscriptionsOnly: false });

      const planByVariant = {};      // variant_id -> parsed selling_plan id
      const familyByVariant = {};    // variant_id -> family (for debugging/guarding)

      liveLines.forEach((line) => {
        const variantId = toNumber(line.dataset.variantId, null);
        const family = line.dataset.productFamily;
        if (!variantId || !family) return;

        const targetPlan = getPlanIdForFamilyQty(line, familyQty[family] || toNumber(line.dataset.quantity, 1));
        const parsed = parseSellingPlanId(targetPlan);
        if (parsed === undefined || parsed === null) return;

        planByVariant[variantId] = parsed;
        familyByVariant[variantId] = family;
      });

      // 2. Read the cart fresh, AFTER the DOM map is built.
      const cartResponse = await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error('Could not read cart');
      const cart = await cartResponse.json();

      // 3. From this single snapshot, build the add list and the remove map.
      const itemsToAdd = [];
      const updatesToRemove = {};

      cart.items.forEach((item) => {
        if (item.selling_plan_allocation) return;            // already a subscription
        const plan = planByVariant[item.variant_id];
        if (plan === undefined || plan === null) return;     // no eligible plan -> leave as-is

        const newItem = {
          id: item.variant_id,
          quantity: item.quantity,
          selling_plan: plan,
          properties: {
            ...(item.properties || {}),
            _upgraded_to_subscription: 'true'
          }
        };

        itemsToAdd.push(newItem);
        updatesToRemove[item.key] = 0;                       // key still valid: nothing re-keyed yet
      });

      // Nothing to convert -> just go to checkout with the cart as-is.
      if (!itemsToAdd.length) {
        if (window.KinokoAtcTracker?.syncBeforeCheckout) {
          try {
            await Promise.race([
              window.KinokoAtcTracker.syncBeforeCheckout(),
              new Promise(resolve => setTimeout(resolve, 700))
            ]);
          } catch (error) {
            console.warn('[Kinoko checkout tracking sync skipped]', error);
          }
        }

        window.location.href = root() + 'checkout';
        return;
      }

      // 4. REMOVE the one-time lines FIRST, while their keys are still valid.
      const removeResponse = await fetch(root() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updatesToRemove })
      });
      if (!removeResponse.ok) {
        const text = await removeResponse.text().catch(() => '');
        throw new Error(text || 'Could not remove one-time items');
      }

      // 5. ADD the subscription lines in one shot, onto the now-clean cart.
      const addResponse = await fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      });
      if (!addResponse.ok) {
        const text = await addResponse.text().catch(() => '');
        throw new Error(text || 'Could not add subscription items');
      }

      if (window.KinokoAtcTracker?.syncBeforeCheckout) {
        try {
          await Promise.race([
            window.KinokoAtcTracker.syncBeforeCheckout(),
            new Promise(resolve => setTimeout(resolve, 700))
          ]);
        } catch (error) {
          console.warn('[Kinoko checkout tracking sync skipped]', error);
        }
      }

      window.location.href = root() + 'checkout';
    }, { loader: true });
  }

  function bindSubscribeCheckout() {
    document.addEventListener('click', async function (event) {
      const button = event.target.closest(SELECTORS.subscribeCheckout);
      if (!button) return;
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      event.preventDefault();

      const originalHTML = button.innerHTML;
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.innerHTML = 'One moment…';

      try {
        await upgradeCartToSubscription();
      } catch (error) {
        console.error('Subscription upgrade failed:', error);
        button.disabled = false;
        button.setAttribute('aria-disabled', 'false');
        button.innerHTML = originalHTML;
        showLoader(false);
      }
    });
  }

  function bindBfcacheRefresh() {
    // When Safari/Chrome restore the page from back/forward cache (e.g. user
    // taps Back from checkout), the DOM is a frozen snapshot — the cart may
    // have changed. Re-render the drawer from live section data.
    //
    // Two restore paths need handling:
    //   1. bfcache restore    → event.persisted === true
    //   2. HTTP/history cache → persisted false, but the navigation entry
    //      reports type 'back_forward' (desktop Chrome after a cross-origin
    //      hop to checkout). Without this, a stale (often empty) cart shows.
    window.addEventListener('pageshow', function (event) {
      const nav = performance.getEntriesByType('navigation')[0];
      const isBackForward = nav && nav.type === 'back_forward';
      if (!event.persisted && !isBackForward) return;
      runCartJob(() => refreshCartDrawer());
    });
  }

  function bindCheckoutGate() {
    document.addEventListener('submit', async function (event) {
      const form = event.target;
      if (!form || form.matches(SELECTORS.checkoutForm) === false) return;

      event.preventDefault();
      showLoader(true);

      try {
        if (isBusy) {
          await Promise.race([
            activeJob,
            new Promise((resolve) => setTimeout(resolve, 4000))
          ]);
        }

        if (window.KinokoAtcTracker?.syncBeforeCheckout) {
          await Promise.race([
            window.KinokoAtcTracker.syncBeforeCheckout(),
            new Promise((resolve) => setTimeout(resolve, 700))
          ]);
        }
      } finally {
        showLoader(false);
        window.location.href = root() + 'checkout';
      }
    }, true);
  }

  function bindDawnRenderHook() {
    const drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer || typeof drawer.renderContents !== 'function' || drawer.__javyRenderPatched) return;

    drawer.__javyRenderPatched = true;
    const originalRenderContents = drawer.renderContents.bind(drawer);

    drawer.renderContents = function (parsedState) {
      originalRenderContents(parsedState);
      document.dispatchEvent(new CustomEvent('javy:cart-drawer-refreshed'));
    };
  }

  function init() {
    // bindDawnRenderHook();
    bindSubscribeCheckout();
    bindCheckoutGate();
    bindBfcacheRefresh();

    // Public API used by cart-drawer-quantity-handler.liquid.
    window.javyCartDrawer = {
      setLineQuantity,
      syncSubscriptionPlans: function () {
        return runCartJob(() => syncSubscriptionPlans({ refresh: true }));
      },
      refreshCartDrawer: function () {
        return runCartJob(() => refreshCartDrawer());
      },
      whenSettled: function () {
        return activeJob.catch(() => {});
      }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
